import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  MessageCircle, RefreshCw, Send, Users, Zap, CheckCircle2, XCircle,
  Clock, ShieldAlert, Filter, ExternalLink,
} from 'lucide-react';
import WhatsAppSendDialog from '@/components/whatsapp/WhatsAppSendDialog';
import WhatsAppBulkDialog from '@/components/whatsapp/WhatsAppBulkDialog';
import BroadcastPanel from '@/components/whatsapp/BroadcastPanel';
import { WhatsAppInbox } from '@/components/whatsapp/WhatsAppInbox';
import { CallLogPanel } from '@/components/calls/CallLogPanel';

export default function CommsPage() {
  const [tab, setTab] = useState('inbox');
  const [config, setConfig] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msgFilter, setMsgFilter] = useState({ template_id: '', patient_mobile: '' });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [openSend, setOpenSend] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);
  const [lapsedPreview, setLapsedPreview] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, k, t, m] = await Promise.all([
        api.get('/whatsapp/config'),
        api.get('/whatsapp/kpi'),
        api.get('/whatsapp/templates'),
        api.get('/whatsapp/messages', { params: { limit: 200 } }),
      ]);
      setConfig(c.data);
      setKpi(k.data);
      setTemplates(t.data.data || []);
      setMessages(m.data.data || []);
    } catch { toast.error('Failed to load Communications data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filteredMessages = useMemo(() => {
    return messages.filter(m => {
      if (msgFilter.template_id && m.template_id !== msgFilter.template_id) return false;
      if (msgFilter.patient_mobile && !(m.to || '').includes(msgFilter.patient_mobile.replace(/\D/g, ''))) return false;
      return true;
    });
  }, [messages, msgFilter]);

  const refreshTemplates = async () => {
    setRefreshing(true);
    try {
      const r = await api.get('/whatsapp/templates', { params: { refresh: true } });
      setTemplates(r.data.data || []);
      if (r.data.remote_synced > 0) toast.success(`Synced ${r.data.remote_synced} templates from Karix`);
      else toast.info('No remote templates available — using local defaults');
    } catch { toast.error('Refresh failed'); }
    finally { setRefreshing(false); }
  };

  const loadLapsedForBulk = async () => {
    try {
      const r = await api.get('/reports/lapsed-patients', { params: { limit: 300, band: '90-180' } });
      const pool = (r.data?.patients || [])
        .filter(p => p.patient_mobile)
        .map(p => ({ to: p.patient_mobile, patient_name: p.patient_name, ticket_id: p.id }));
      setLapsedPreview(pool);
      if (pool.length === 0) toast.warning('No lapsed patients with phone numbers');
      else setOpenBulk(true);
    } catch { toast.error('Failed to load lapsed patients'); }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-stone-50" data-testid="comms-page">
      <div className="max-w-[1400px] w-full mx-auto p-4 space-y-4 flex-1 overflow-auto hc-scrollbar">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1.5 text-emerald-700">
              <MessageCircle className="w-3 h-3" /> Communications · WhatsApp
            </div>
            <h1 className="font-heading text-2xl font-bold hc-section-title">Patient Communications</h1>
            <p className="text-sm text-stone-500 mt-0.5">
              Send single & bulk WhatsApp messages via Karix, review templates, and audit every delivery.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} className="h-8" data-testid="comms-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setOpenSend(true)} data-testid="comms-send-btn">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Send WhatsApp
            </Button>
          </div>
        </div>

        {/* Karix status */}
        <Card className={`p-3 border ${config?.enabled ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
          data-testid="comms-status-card">
          <div className="flex items-center gap-3">
            {config?.enabled ? <CheckCircle2 className="w-5 h-5 text-emerald-700" /> : <ShieldAlert className="w-5 h-5 text-amber-700" />}
            <div className="flex-1">
              <div className={`text-sm font-semibold ${config?.enabled ? 'text-emerald-900' : 'text-amber-900'}`}>
                Karix WhatsApp API — {config?.enabled ? 'Connected' : 'Not configured'}
              </div>
              <div className="text-xs text-stone-600 mt-0.5 flex gap-3 flex-wrap">
                <span>Sender: <b className="font-mono">{config?.sender || '—'}</b></span>
                <span>Base: <b className="font-mono">{config?.base_url || '—'}</b></span>
                <span>Webhook DNID: <b className="font-mono">{config?.webhook_dnid || '—'}</b></span>
              </div>
              {!config?.enabled && (
                <div className="text-[11px] text-amber-800 mt-1">
                  Set <code>KARIX_API_KEY</code> in <code>backend/.env</code> (generate it from the Karix Lounge → API Keys).
                  Until then all sends will be logged as <code>skipped</code>.
                </div>
              )}
            </div>
            {config?.lounge_url && (
              <a href={config.lounge_url} target="_blank" rel="noreferrer"
                 className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-md border border-stone-300 bg-white hover:bg-stone-50 text-xs font-medium text-stone-700"
                 data-testid="comms-lounge-link"
                 title={config.lounge_user ? `Log in as ${config.lounge_user}` : 'Karix Lounge'}>
                <ExternalLink className="w-3.5 h-3.5" /> Open Karix Lounge
              </a>
            )}
          </div>
        </Card>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <KPI label="Total Sent" value={kpi?.sent} icon={Send} tone="success" tid="comms-kpi-sent" />
          <KPI label="Delivered" value={kpi?.delivered} icon={CheckCircle2} tone="info" tid="comms-kpi-delivered" />
          <KPI label="Failed" value={kpi?.failed} icon={XCircle} tone="danger" tid="comms-kpi-failed" />
          <KPI label="Total Logged" value={kpi?.total} icon={Clock} tone="default" tid="comms-kpi-total" />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 h-auto md:h-9">
            <TabsTrigger value="inbox" data-testid="comms-tab-inbox">Inbox</TabsTrigger>
            <TabsTrigger value="send" data-testid="comms-tab-send">Send</TabsTrigger>
            <TabsTrigger value="broadcast" data-testid="comms-tab-broadcast">Broadcast</TabsTrigger>
            <TabsTrigger value="templates" data-testid="comms-tab-templates">Templates ({templates.length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="comms-tab-history">History ({messages.length})</TabsTrigger>
            <TabsTrigger value="calls" data-testid="comms-tab-calls">AI Calls</TabsTrigger>
          </TabsList>

          <TabsContent value="inbox" className="mt-3">
            <WhatsAppInbox />
          </TabsContent>

          <TabsContent value="calls" className="mt-3">
            <CallLogPanel />
          </TabsContent>

          {/* ---- SEND ---- */}
          <TabsContent value="send" className="mt-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card className="p-4 border-stone-200 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded bg-emerald-100 flex items-center justify-center shrink-0">
                    <Send className="w-4 h-4 text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-stone-900">Single message</h3>
                    <p className="text-xs text-stone-500 mt-0.5">Pick a template, choose a recipient, hit send. Great for confirmations & 1:1 nudges.</p>
                    <Button className="mt-3 h-8 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setOpenSend(true)} data-testid="comms-single-cta">
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Compose
                    </Button>
                  </div>
                </div>
              </Card>

              <Card className="p-4 border-stone-200 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded bg-amber-100 flex items-center justify-center shrink-0">
                    <Zap className="w-4 h-4 text-amber-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-stone-900">Lapsed re-engagement</h3>
                    <p className="text-xs text-stone-500 mt-0.5">Send the reactivation template to patients idle 90–180 days (up to 300 recipients).</p>
                    <Button className="mt-3 h-8 bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={loadLapsedForBulk} data-testid="comms-lapsed-cta">
                      <Users className="w-3.5 h-3.5 mr-1.5" /> Bulk from Lapsed 90–180d
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* ---- BROADCAST ---- */}
          <TabsContent value="broadcast" className="mt-3 space-y-3">
            <BroadcastPanel templates={templates} />
          </TabsContent>

          {/* ---- TEMPLATES ---- */}
          <TabsContent value="templates" className="mt-3 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-500">{templates.length} active templates</span>
              <div className="flex-1" />
              <Button variant="outline" size="sm" onClick={refreshTemplates} className="h-8" data-testid="comms-refresh-templates">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                Sync from Karix
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map(t => (
                <Card key={t.template_id} className="p-3 border-stone-200" data-testid={`comms-template-${t.template_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-stone-900 truncate">{t.name}</div>
                      <div className="text-[11px] font-mono text-stone-500 truncate">{t.template_id}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                      {t.category}
                    </Badge>
                  </div>
                  {t.description && <div className="text-xs text-stone-600 mt-2">{t.description}</div>}
                  {(t.parameters || []).length > 0 && (
                    <div className="text-[10px] text-stone-500 mt-2">
                      Variables: {t.parameters.map(p => `{{${Number(p.index) + 1}}} ${p.label}`).join(' · ')}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ---- HISTORY ---- */}
          <TabsContent value="history" className="mt-3 space-y-3">
            <Card className="p-3 border-stone-200">
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-3.5 h-3.5 text-stone-500" />
                <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Filter</span>
                <Select value={msgFilter.template_id || 'all'} onValueChange={(v) => setMsgFilter(f => ({ ...f, template_id: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-8 w-52 text-xs" data-testid="comms-history-tpl-filter"><SelectValue placeholder="All templates" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All templates</SelectItem>
                    {templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input value={msgFilter.patient_mobile} onChange={(e) => setMsgFilter(f => ({ ...f, patient_mobile: e.target.value }))}
                  placeholder="Filter by mobile…" className="h-8 w-40 text-xs" data-testid="comms-history-mobile-filter" />
                <div className="flex-1" />
                <span className="text-[10px] text-stone-500">{filteredMessages.length} of {messages.length}</span>
              </div>
            </Card>

            <Card className="border-stone-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="comms-history-table">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      {['When', 'To', 'Template', 'Patient', 'Status', 'Sent by'].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-semibold text-stone-600 text-[10px] uppercase tracking-widest">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMessages.slice(0, 200).map(m => (
                      <tr key={m.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`comms-msg-${m.id}`}>
                        <td className="px-3 py-1.5 tabular-nums text-stone-600">{formatWhen(m.created_at)}</td>
                        <td className="px-3 py-1.5 font-mono">{m.to}</td>
                        <td className="px-3 py-1.5">
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-700 font-mono">{m.template_id}</span>
                        </td>
                        <td className="px-3 py-1.5 text-stone-700">{m.patient_name || '—'}</td>
                        <td className="px-3 py-1.5">
                          <StatusPill status={m.status} deliveryStatus={m.delivery_status} />
                        </td>
                        <td className="px-3 py-1.5 text-stone-500 text-[11px]">{m.sent_by || '—'}</td>
                      </tr>
                    ))}
                    {filteredMessages.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">No messages yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <WhatsAppSendDialog open={openSend} onOpenChange={setOpenSend} onSent={load} />
      <WhatsAppBulkDialog open={openBulk} onOpenChange={setOpenBulk} recipients={lapsedPreview}
        defaultTemplateId="lapsed_reengage" onSent={load} />
    </div>
  );
}

function KPI({ label, value, icon: Icon, tone = 'default', tid }) {
  const tones = {
    default: 'bg-white border-stone-200 text-stone-800',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    danger: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-sky-50 border-sky-200 text-sky-800',
  }[tone];
  return (
    <div className={`p-3 border rounded-md ${tones}`} data-testid={tid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-60" />
      </div>
      <div className="font-heading font-bold text-xl tabular-nums">
        {value == null ? '—' : value.toLocaleString()}
      </div>
    </div>
  );
}

function StatusPill({ status, deliveryStatus }) {
  const s = (deliveryStatus || status || '').toLowerCase();
  const cfg = {
    sent: ['bg-sky-100 text-sky-800', 'Sent'],
    delivered: ['bg-emerald-100 text-emerald-800', 'Delivered'],
    read: ['bg-emerald-200 text-emerald-900', 'Read'],
    failed: ['bg-red-100 text-red-800', 'Failed'],
    skipped: ['bg-amber-100 text-amber-800', 'Skipped'],
    queued: ['bg-stone-100 text-stone-700', 'Queued'],
  }[s] || ['bg-stone-100 text-stone-700', s || '—'];
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider ${cfg[0]}`}>{cfg[1]}</span>;
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
